/**
 * LCStatusBadge Component
 *
 * Displays the status of a liquid culture with appropriate styling.
 * Uses the design system's neutral-first approach with subtle status colours.
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Circle, CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react'

// Status configuration with colours and icons
const statusConfig = {
  incubating: {
    label: 'Incubating',
    icon: Clock,
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  ready: {
    label: 'Ready',
    icon: CheckCircle,
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  in_use: {
    label: 'In Use',
    icon: Circle,
    className: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  finished: {
    label: 'Finished',
    icon: CheckCircle,
    className: 'bg-gray-50 text-gray-600 border-gray-200'
  },
  contaminated: {
    label: 'Contaminated',
    icon: XCircle,
    className: 'bg-red-50 text-red-700 border-red-200'
  }
}

/**
 * Status badge for liquid cultures
 * @param {Object} props
 * @param {string} props.status - The LC status
 * @param {boolean} [props.showIcon=true] - Whether to show the icon
 * @param {string} [props.className] - Additional CSS classes
 */
export default function LCStatusBadge({ status, showIcon = true, className }) {
  const config = statusConfig[status] || {
    label: status,
    icon: Circle,
    className: 'bg-gray-50 text-gray-600 border-gray-200'
  }

  const Icon = config.icon

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium gap-1.5',
        config.className,
        className
      )}
    >
      {showIcon && <Icon className="h-3 w-3" aria-hidden="true" />}
      {config.label}
    </Badge>
  )
}

/**
 * Generation badge component
 * @param {Object} props
 * @param {number} props.generation - The generation number (1, 2, or 3)
 * @param {string} [props.className] - Additional CSS classes
 */
export function GenerationBadge({ generation, className }) {
  const isG3 = generation === 3

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-mono font-semibold',
        isG3
          ? 'bg-orange-50 text-orange-700 border-orange-200'
          : 'bg-gray-50 text-gray-700 border-gray-200',
        className
      )}
    >
      G{generation}
      {isG3 && (
        <AlertCircle className="h-3 w-3 ml-1" aria-label="Generation 3 - consider creating fresh master" />
      )}
    </Badge>
  )
}
